import React from 'react';
import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { Conditional } from 'Views/Components/Conditional/Conditional';
import PdfBreak from 'Views/Pages/Pdf/Components/PDFBreak';
import { CourseEntity } from 'Models/Entities';

interface PdfCourseAccordionProps<T> {
	title: string,
	depth?: number;
	items: T[],
	renderItem: (item: T, index: number) => React.ReactNode,
	hideTitle?: boolean,
	emptyLabel?: string;
	unitCount?: number;
	unitMinimum?: number;
	description?: React.ReactNode;
	errorMessage?: React.ReactNode;
}

const styles = StyleSheet.create({
	title: {
		fontSize: 8,
		backgroundColor: '#F2F2F2',
		padding: 4,
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
});

const PdfCourseAccordion = <T, >(props: PdfCourseAccordionProps<T>) => {
	const {
		title,
		hideTitle,
		items,
		renderItem,
		emptyLabel = 'No data',
		unitCount,
		unitMinimum,
		description,
		errorMessage,
		depth,
	} = props;

	const isTopLevel = depth === 0;

	const hasNoChildren = items.length === 0;
	const containsCourses = items.length > 0 && items[0] instanceof CourseEntity;

	// Disable component wrapping when
	// - There are no children
	// OR
	// - Children are courses
	const wrapComponent = !hasNoChildren && !containsCourses;

	return (
		<>
			{/* This is a hack to get the wrapping to work correctly (not squishing/compressing other components) */}
			{/* eslint-disable-next-line react/jsx-no-comment-textnodes */}
			<View />

			<View wrap={wrapComponent}>

				{/* This is a hack to get the wrapping to work correctly */}
				<View />

				{/* Disabled wrapping here so the title, description and error are always together on the same page */}
				<View wrap={false}>
					{!hideTitle && (
						<View
							style={[
								styles.title,
								isTopLevel ? { fontWeight: 'bold', textTransform: 'uppercase' } : {},
							]}
						>
							<Text>{title}</Text>
							<Conditional
								condition={unitCount}
								component={() => (
									<Text>{unitCount}{unitMinimum !== undefined ? `/${unitMinimum}` : null}</Text>
								)}
								componentProps={{}}
							/>
						</View>
					)}
					{description}
					{errorMessage}
				</View>

				<PdfBreak height={8} />

				<View style={{ display: 'flex', gap: 8 }}>
					{
						items?.length
							? items.map((item, index) => renderItem(item, index))
							: (
								<Text>{emptyLabel}</Text>
							)
					}
				</View>
			</View>
		</>
	);
};

export default PdfCourseAccordion;
